<template>
  <div class="batch-stats-page">
    <div class="card">
      <div class="head">
        <el-button icon="el-icon-arrow-left" @click="goBack">{{ $t('actions.back') }}</el-button>
        <div>
          <h2 class="title">{{ isEn ? 'Selected Key Slice Statistics' : '选中病例关键张统计' }}</h2>
          <p class="desc">
            {{ isEn
              ? `Using hu_statistics_middle_only.csv from ${selectedPatients.length} selected case(s).`
              : `使用 ${selectedPatients.length} 个选中病例的 hu_statistics_middle_only.csv 进行统计。` }}
          </p>
        </div>
        <div class="spacer"></div>
        <el-button size="small" type="primary" :disabled="!statsRows.length" @click="downloadStatsCsv">
          {{ isEn ? 'Download Statistics' : '下载统计结果' }}
        </el-button>
        <el-button size="small" type="primary" plain :loading="loading" @click="fetchStats">
          {{ isEn ? 'Refresh Statistics' : '刷新统计' }}
        </el-button>
      </div>

      <el-alert
        v-if="warnings.length"
        class="warning-box"
        type="warning"
        show-icon
        :closable="false"
        :title="isEn ? `${warnings.length} case(s) skipped` : `${warnings.length} 个病例已跳过`"
      >
        <div v-for="item in warnings" :key="item">{{ item }}</div>
      </el-alert>

      <el-table
        :data="statsRows"
        border
        size="small"
        v-loading="loading"
        :empty-text="isEn ? 'No statistics data' : '暂无统计数据'"
        style="width: 100%"
      >
        <el-table-column prop="label" :label="isEn ? 'Metric' : '指标'" min-width="240" />
        <el-table-column prop="mean" :label="isEn ? 'Mean' : '平均值'" width="140" />
        <el-table-column prop="max" :label="isEn ? 'Max' : '最大值'" width="140" />
        <el-table-column prop="min" :label="isEn ? 'Min' : '最小值'" width="140" />
        <el-table-column prop="median" :label="isEn ? 'Median' : '中位数'" width="140" />
        <el-table-column prop="count" :label="isEn ? 'Sample Count' : '样本数'" width="120" />
      </el-table>

      <div class="selected-list">
        <div class="sub-title">{{ isEn ? 'Selected Cases' : '选中病例' }}</div>
        <el-tag
          v-for="patient in selectedPatients"
          :key="patient.display || `${patient.patient_name}-${patient.study_date}`"
          size="small"
          class="case-tag"
        >
          {{ patient.patient_name }} - {{ patient.study_date }}
        </el-tag>
      </div>
    </div>
  </div>
</template>

<script>
import { getMiddleOnlyStatisticsSummary } from "@/api";

const METRICS = [
  ["Psoas HU(mean)", "psoas_hu_mean"],
  ["Psoas Area(mm2)", "psoas_area_mm2"],
  ["Psoas Volume(mm3)", "psoas_volume_mm3"],
  ["Combo HU(mean)", "combo_hu_mean"],
  ["Combo Area(mm2)", "combo_area_mm2"],
  ["Total Muscle Volume(mm3)", "full_muscle_volume_mm3"],
  ["SAT HU", "sat_hu_mean"],
  ["SAT Area(mm2)", "sat_area_mm2"],
  ["VAT HU", "vat_hu_mean"],
  ["VAT Area(mm2)", "vat_area_mm2"],
];

const METRICS_ZH = [
  ["Psoas HU(mean)", "psoas_hu_mean"],
  ["Psoas 面积(mm2)", "psoas_area_mm2"],
  ["腰大肌体积(mm3)", "psoas_volume_mm3"],
  ["Combo HU(mean)", "combo_hu_mean"],
  ["Combo 面积(mm2)", "combo_area_mm2"],
  ["全肌肉体积(mm3)", "full_muscle_volume_mm3"],
  ["SAT HU", "sat_hu_mean"],
  ["SAT 面积(mm2)", "sat_area_mm2"],
  ["VAT HU", "vat_hu_mean"],
  ["VAT 面积(mm2)", "vat_area_mm2"],
];

export default {
  name: "BatchStatisticsResult",
  data() {
    return {
      loading: false,
      selectedPatients: [],
      statsSummary: null,
      warnings: [],
      ownerId: null,
      ownerName: "",
    };
  },
  computed: {
    isEn() {
      return this.$i18n.locale === "en";
    },
    metricDefs() {
      return this.isEn ? METRICS : METRICS_ZH;
    },
    statsRows() {
      if (!this.statsSummary) return [];
      const metrics = this.statsSummary.metrics || {};
      return this.metricDefs.map(([label, key]) => {
        const item = metrics[key] || {};
        return {
          label,
          mean: this.fmt(item.mean),
          max: this.fmt(item.max),
          min: this.fmt(item.min),
          median: this.fmt(item.median),
          count: Array.isArray(item.values) ? item.values.length : this.statsSummary.count || 0,
        };
      });
    },
  },
  created() {
    this.loadPayload();
    this.fetchStats();
  },
  methods: {
    loadPayload() {
      const batchId = this.$route.query.batch_id;
      const raw = batchId ? sessionStorage.getItem(batchId) : "";
      if (!raw) {
        this.selectedPatients = [];
        this.ownerId = this.$route.query.owner_id || null;
        this.ownerName = this.$route.query.owner_name || "";
        return;
      }
      try {
        const payload = JSON.parse(raw);
        this.selectedPatients = Array.isArray(payload.patients) ? payload.patients : [];
        this.ownerId = payload.ownerId || this.$route.query.owner_id || null;
        this.ownerName = payload.ownerName || this.$route.query.owner_name || "";
      } catch (e) {
        this.selectedPatients = [];
      }
    },
    async fetchStats() {
      if (!this.selectedPatients.length) {
        this.$message.warning(this.isEn ? "No selected cases found" : "未找到选中的病例");
        return;
      }
      this.loading = true;
      try {
        const patients = this.selectedPatients.map((row) => ({
          patient_name: row.patient_name,
          study_date: row.study_date,
        }));
        const res = await getMiddleOnlyStatisticsSummary(this.ownerId, patients);
        this.statsSummary = (res && res.data) || null;
        this.warnings = (this.statsSummary && this.statsSummary.warnings) || [];
      } catch (e) {
        this.statsSummary = null;
        this.warnings = [];
        this.$message.error(e.response?.data?.detail || (this.isEn ? "Batch statistics failed" : "批量统计失败"));
      } finally {
        this.loading = false;
      }
    },
    fmt(value) {
      return value == null || Number.isNaN(Number(value)) ? "-" : Number(value).toFixed(2);
    },
    downloadStatsCsv() {
      if (!this.statsRows.length) return;
      const header = this.isEn
        ? ["Metric", "Mean", "Max", "Min", "Median", "Sample Count"]
        : ["指标", "平均值", "最大值", "最小值", "中位数", "样本数"];
      const escapeCell = (value) => `"${String(value == null ? "" : value).replace(/"/g, '""')}"`;
      const lines = [
        header.map(escapeCell).join(","),
        ...this.statsRows.map((row) =>
          [row.label, row.mean, row.max, row.min, row.median, row.count].map(escapeCell).join(",")
        ),
      ];
      const blob = new Blob(["\uFEFF", lines.join("\r\n")], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14);
      link.href = url;
      link.download = `batch_key_slice_statistics_${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    goBack() {
      const query = { scope: "all" };
      if (this.ownerId) query.owner_id = this.ownerId;
      if (this.ownerName) query.owner_name = this.ownerName;
      this.$router.push({ path: "/results/statistics", query });
    },
  },
};
</script>

<style scoped>
.batch-stats-page {
  width: clamp(960px, 86vw, 1140px);
  margin: 24px auto 80px;
}

.card {
  background: #ffffff;
  border-radius: 14px;
  padding: 20px 16px 18px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
}

.head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.title {
  margin: 0;
  font-size: 22px;
  color: #132033;
}

.desc {
  margin: 4px 0 0;
  color: #5f6c7b;
}

.spacer {
  flex: 1;
}

.warning-box {
  margin-bottom: 14px;
}

.selected-list {
  margin-top: 16px;
}

.sub-title {
  margin-bottom: 8px;
  font-weight: 700;
  color: #344456;
}

.case-tag {
  margin: 0 8px 8px 0;
}
</style>
